import { EgmCardStatus, useWealthOverview } from '@egw/api'
import { Alert, AlertType, Button, ButtonVariant, GenericError, ModalDrawer, PageHeader, useModalState } from '@egw/components'
import { useIsMobileScreen } from '@egw/components/src/popover/hooks/layout-hook'
import { WEALTH_ROUTES } from '@egw/constants'
import { sendEvent } from '@egw/events'
import { useTranslation } from '@egw/i18n'
import { type FC, useEffect, useState } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useNavigate } from 'react-router'
import ActivatePhysicalCard from '../../components/activate-physical-card'
import CardFront from '../../components/card-front/card-front'

export interface DebitCardActivateProps {
  alias: string
}

export const DebitCardActivate: FC<DebitCardActivateProps> = ({ alias }) => {
  const { t } = useTranslation('features-account')
  const isMobileScreen = useIsMobileScreen()

  const navigate = useNavigate()

  const { data: wealthOverviewData, isLoading, error } = useWealthOverview()

  const cardData = wealthOverviewData?.highYieldChecking?.accounts.find((account) => account.alias === parseInt(alias))

  const { isOpen, openModal, closeModal } = useModalState()
  const [modalTitle, setModalTitle] = useState<string>(t('debit_card.activate.title'))

  const needsPin = cardData?.debitCard[0]?.needToSetPin
  const cardIsActivated = cardData?.debitCard[0]?.cardStatus === EgmCardStatus.ACTIVE

  useEffect(() => {
    if (cardIsActivated && !needsPin) {
      void navigate(WEALTH_ROUTES.CHECKING_ACCOUNT_DEBIT_CARD_ACTIVATE_SUCCESS.path.replace(':alias', alias.toString()))
    } else if (cardIsActivated && needsPin && !isOpen) {
      void navigate(WEALTH_ROUTES.CHECKING_ACCOUNT_DEBIT_CARD_SET_PIN.path.replace(':alias', alias.toString()))
    }
  }, [navigate, cardIsActivated, needsPin, alias, isOpen])

  const onModalTitle = (title: string): void => {
    setModalTitle(title)
  }

  return (
    <>
      {isLoading ? (
        <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
          <div className="egw-container">
            <Skeleton count={1} height="60px" />
          </div>
          <Skeleton count={1} height="270px" />
          <div className="egw-container">
            <Skeleton count={5} height="40px" className="mt-1" />
          </div>
        </SkeletonTheme>
      ) : null}

      {!isLoading ? (
        <PageHeader
          title={t('debit_card.activate.title')}
          subtitle={t('debit_card.title')}
          backLinkUrl={WEALTH_ROUTES.CHECKING_ACCOUNT_DEBIT_CARD_ACTIVATE.backPath?.replace(':alias', alias.toString())}
        />
      ) : null}

      {cardData ? (
        <>
          <div className="py-8 pb-12 md:bg-gray-50">
            <div className="default-page-width flex flex-col align-center justify-center items-center">
              <div className="justify-center align-center flex flex-col items-center px-3">
                <CardFront className="opacity-40 max-w-[340px]" width={isMobileScreen ? '100%' : 340} />
              </div>
            </div>
          </div>
          <div className="flex flex-col items-center">
            <div className="justify-center align-center flex flex-col items-center shadow rounded p-6 -mt-6 mx-[10%] sm:mx-0 bg-white sm:w-[340px] max-w-[340px]">
              <Alert alertType={AlertType.Info} description={t('debit_card.activate.have_physical_card')} />

              <Button
                variant={ButtonVariant.Primary}
                id="activateCard"
                className="mb-4 md:mb-0 w-full mt-6"
                onClick={() => {
                  sendEvent('user_cardActivate_click', {
                    eventSource: 'egWealth',
                    category: 'card-activation',
                    subCategory: 'intent',
                  })
                  openModal()
                }}
              >
                {t('debit_card.activate.title')}
              </Button>
            </div>
          </div>
          <ModalDrawer isOpen={isOpen} onClose={closeModal} title={modalTitle}>
            <ActivatePhysicalCard
              closeModal={closeModal}
              cardId={cardData.debitCard[0].cardId}
              setModalTitle={onModalTitle}
              alias={alias}
            />
          </ModalDrawer>
        </>
      ) : null}
      {Boolean(error) && (
        <div className="egw-container">
          <GenericError errorMessage={t('common.generic_error')} />
        </div>
      )}
    </>
  )
}
